/**
 * Unified layout constants for the BaseState UI.
 * Single source of truth for spacing, typography, and color tokens.
 */
import type { Graphics } from 'pixi.js';
import { drawBevelPanel, GUGI_SHADOW, type BevelPanelOptions } from './PanelUtils';
import type { ViewportInfo } from './Viewport';

// ─── Accent palette ─────────────────────────────────────────
export const ACCENT       = 0xDD2233;
export const ACCENT_MED   = 0xBB1122;
export const ACCENT_DIM   = 0x881122;
export const ACCENT_DARK  = 0x2a1015;

// ─── Screen & spacing ───────────────────────────────────────
export const MARGIN    = 12;   // outer margin from screen edges
export const GAP       = 10;   // gap between panels
export const PAD_S     = 8;    // small inner padding
export const PAD_M     = 12;   // medium inner padding
export const PAD_L     = 16;   // large inner padding

// ─── Three-column grid (desktop landscape) ──────────────────
// These defaults are the desktop values. Phase 2+ callers should migrate to the
// `vp`-aware helpers below so the layout can collapse on tablet/mobile.
export const LEFT_W    = 360;
export const RIGHT_W   = 360;
// CENTER_W is computed from screen width at runtime
export function centerW(screenW: number) { return screenW - LEFT_W - RIGHT_W - MARGIN * 2 - GAP * 2; }

export const COL_LEFT_X  = MARGIN;
export function colCenterX() { return MARGIN + LEFT_W + GAP; }
export function colRightX(screenW: number) { return screenW - MARGIN - RIGHT_W; }

export const CONTENT_Y = 108;  // top of content area (below logo row)
export function bottomY(screenH: number) { return screenH - 50; }

// ─── Viewport-aware helpers ─────────────────────────────────
// Use these in place of LEFT_W / RIGHT_W / centerW / colRightX when rebuilding
// a layout that needs to adapt to tablet or mobile sizes. On desktop they
// return values identical to the constants above, so desktop rendering is
// byte-for-byte unchanged.

/** Left/right panel width for the current viewport. Full viewport width on mobile. */
export function leftPanelW(vp: ViewportInfo): number {
  if (vp.mode === 'mobile') return vp.w - MARGIN * 2;
  if (vp.mode === 'tablet') return 300;
  return LEFT_W;
}
export function rightPanelW(vp: ViewportInfo): number {
  if (vp.mode === 'mobile') return vp.w - MARGIN * 2;
  if (vp.mode === 'tablet') return 300;
  return RIGHT_W;
}

/** Center column width. On mobile the whole content area is "center". */
export function centerWVP(vp: ViewportInfo): number {
  if (vp.mode === 'mobile') return vp.w - MARGIN * 2;
  return vp.w - leftPanelW(vp) - rightPanelW(vp) - MARGIN * 2 - GAP * 2;
}

/** Left/center/right column X origin for the current viewport. */
export function colLeftXVP(_vp: ViewportInfo): number { return MARGIN; }
export function colCenterXVP(vp: ViewportInfo): number {
  if (vp.mode === 'mobile') return MARGIN;
  return MARGIN + leftPanelW(vp) + GAP;
}
export function colRightXVP(vp: ViewportInfo): number {
  if (vp.mode === 'mobile') return MARGIN;
  return vp.w - MARGIN - rightPanelW(vp);
}

/** Height of the mobile bottom tab bar (0 when not in mobile mode). */
export function tabBarH(vp: ViewportInfo): number {
  return vp.mode === 'mobile' ? 64 + vp.safeBottom : 0;
}

/** Height of the compact top status strip (0 when not in mobile mode). */
export function topStatusH(vp: ViewportInfo): number {
  return vp.mode === 'mobile' ? 56 + vp.safeTop : 0;
}

// ─── Typography hierarchy ───────────────────────────────────
export const FONT_HEADER    = { fontFamily: 'Gugi', fontSize: 16 } as const;
export const FONT_SUBHEADER = { fontFamily: 'Gugi', fontSize: 13 } as const;
export const FONT_BODY      = { fontFamily: 'Offside', fontSize: 14 } as const;
export const FONT_DETAIL    = { fontFamily: 'Offside', fontSize: 12 } as const;
export const FONT_SMALL     = { fontFamily: 'Offside', fontSize: 10 } as const;

// Re-export for convenience
export { GUGI_SHADOW };

// ─── Text color hierarchy ───────────────────────────────────
export const TEXT_PRIMARY   = 0x8899aa;
export const TEXT_SECONDARY = 0x667788;
export const TEXT_DIM       = 0x445566;
export const TEXT_MUTED     = 0x334455;

// ─── Panel style ────────────────────────────────────────────
export const PANEL_FILL    = 0x0c0e14;
export const PANEL_RADIUS  = 6;
export const INSET_FILL    = 0x12161e;
export const INSET_RADIUS  = 4;

/** Standard panel options — all panels share this base style. */
export const STANDARD_PANEL_OPTS: BevelPanelOptions = {
  fill: PANEL_FILL,
  border: ACCENT_DIM,
  radius: PANEL_RADIUS,
  accentEdge: ACCENT,
  rivets: true,
};

/** Draw a panel with the unified project style. */
export function drawStandardPanel(g: Graphics, x: number, y: number, w: number, h: number, opts?: Partial<BevelPanelOptions>) {
  drawBevelPanel(g, x, y, w, h, { ...STANDARD_PANEL_OPTS, ...opts });
}

// ─── Equipment pentagram ────────────────────────────────────
export const PENTA_RADIUS = 200;

/** Category order for pentagram slots (top-first, clockwise). */
const PENTA_CATEGORIES = ['sensors', 'shield', 'engine', 'chassis', 'capacitor'] as const;

/** Compute pentagram slot positions for a given radius. */
export function computePentagramSlots(radius: number = PENTA_RADIUS) {
  return PENTA_CATEGORIES.map((category, i) => {
    const angle = (i * 2 * Math.PI / 5) - Math.PI / 2;
    return {
      category,
      dx: Math.round(radius * Math.cos(angle)),
      dy: Math.round(radius * Math.sin(angle)),
    };
  });
}
